// sim/evolutionBoxExample.ts — headless Evolution-Box demo + the world builder.
//
// `buildEvolutionWorld(config)` MIRRORS buildColonyWorld's wiring EXACTLY (same
// SoA store, grid, genomes, colonies, pathing, genes, tasks, AI, organism loop,
// resources — drawn from the colony RNG in the SAME order, so colony determinism
// is byte-identical) and then layers the ADDITIVE economy: an EvolutionBoxSystem
// driven by an INDEPENDENT RNG stream (so box randomness never perturbs the
// colony) plus the two zero-dep seams — ctx.mutation (kill energy in) and a
// ColonyEnergySource (exploration income out).
//
// The determinism test and the CLI both import this, so the demo and the test
// exercise ONE code path.
//
// Run it directly:  node src/sim/evolutionBoxExample.ts --colonies 2 --ticks 300 --map

import {
  CASTE,
  RARITY,
  BOX_TIER_COUNT,
  NIL,
} from '../types.ts';
import type {
  ColonyWorldConfig,
  EvolutionBoxConfig,
  MutationEnergySink,
  System,
  Tick,
} from '../types.ts';
import { Rng } from '../rng.ts';
import { fnvInit, fnvU32, hashHex } from '../hash.ts';
import { EntitySystem } from '../engine/EntitySystem.ts';
import { WorldGrid } from '../engine/WorldGrid.ts';
import { GameLoop } from '../engine/GameLoop.ts';
import { GreedyPathing } from '../engine/Pathing.ts';
import { ResourceSystem } from './resources.ts';
import { ColonySystem } from './colony.ts';
import { OrganismSystem } from './organisms.ts';
import { TaskQueue } from './taskQueue.ts';
import type { AutonomyCtx } from './taskQueue.ts';
import { ColonyAISystem } from './colonyAI.ts';
import { GenomeSystem, baseProfile } from './genome.ts';
import { GeneInventory } from './genes.ts';
import { EvolutionBoxSystem } from './evolutionBox.ts';
import type { ColonyEnergySource } from './evolutionBox.ts';
import {
  defaultCasteConfigs,
  defaultColonyConfig,
  defaultGeneConfig,
  defaultColonyAIConfig,
  defaultLeaderboardConfig,
  computeColonyStateHash,
  renderColony,
  AI_STREAM_SALT,
} from './colonyExample.ts';
import type { ColonyWorld } from './colonyExample.ts';
import { LeaderboardSystem } from './leaderboard.ts';

// ---------------------------------------------------------------------------
// World container — a structural SUPERSET of ColonyWorld (so the shared
// computeColonyStateHash accepts it) plus the Evolution-Box layer.
// ---------------------------------------------------------------------------

export interface EvolutionWorld extends ColonyWorld {
  /** The Evolution-Box economy system (loot boxes + mutation energy). */
  readonly evolution: EvolutionBoxSystem;
  /** The INDEPENDENT RNG stream feeding box opens/forges. */
  readonly evolutionRng: Rng;
}

// ---------------------------------------------------------------------------
// Default tuning (shared by the CLI and tests). EXACT values per the spec.
// ---------------------------------------------------------------------------

/**
 * Evolution-Box economy defaults. All integer; tiers ascend Common→Apex→Miracle.
 * PACING — evolution-box generation speed. Mutation energy is earned from kills
 * and from periodic exploration income; box COSTS are left untouched so opening one
 * still feels the same — you simply earn toward it ~5x slower. incomeInterval 8 → 40
 * (5x) stretches the exploration-income clock and energyPerKill 6 → 3 (0.5x) halves
 * the per-kill payout, so boxes become an occasional event, not a constant drip.
 */
export function defaultEvolutionBoxConfig(): EvolutionBoxConfig {
  return {
    energyPerKill: 3,
    exploreEnergyPerFlyer: 1,
    incomeInterval: 40,
    sacrificeInterval: 20,
    keepPerGroup: 1,
    pityThreshold: 5,
    rareRarityFloor: RARITY.Rare,
    rareQualityBonus: 4,
    boxCost: [20, 45, 90],
    guaranteedGenes: [1, 2, 3],
    openQualityBonus: [0, 2, 4],
    rareChancePct: [10, 25, 45],
    tierUpAfter: [4, 6],
    sacrificeYield: [2, 4, 8, 16, 32], // by RARITY.* Common→Legendary
  };
}

// ---------------------------------------------------------------------------
// Colony-energy seam: colony count + per-colony live-Flyer count, read lazily
// from the ColonySystem (no cached array) so it stays allocation-free + live.
// ---------------------------------------------------------------------------

function buildEnergySource(colonies: ColonySystem): ColonyEnergySource {
  return {
    colonyCount(): number {
      return colonies.count;
    },
    flyerCountOf(cid: number): number {
      return colonies.casteCountAt(cid, CASTE.Flyer);
    },
  };
}

// ---------------------------------------------------------------------------
// World builder.
// ---------------------------------------------------------------------------

export function buildEvolutionWorld(
  config: ColonyWorldConfig & { evolution: EvolutionBoxConfig },
): EvolutionWorld {
  // The colony RNG stream. Drawing walls/food/colonies from it in the SAME order
  // as buildColonyWorld keeps the colony layer byte-identical. The box layer
  // draws from a SEPARATE stream (below).
  const rng = new Rng(config.seed);

  const peakPerColony = Math.max(config.colony.maxPopulation, config.startMembers);
  const capacity = config.colonies * peakPerColony + config.food + 16;

  const entities = new EntitySystem(capacity);
  const grid = new WorldGrid(entities, config.width, config.height, config.maxPerTile);
  const resources = new ResourceSystem(entities, grid, config.resource, config.food);

  const baseProfiles = config.castes.map((cfg, caste) => baseProfile(cfg, caste === CASTE.Flyer));
  const genomes = new GenomeSystem(baseProfiles);

  const maxColonies = Math.max(1, config.colonies);
  const colonies = new ColonySystem(entities, grid, genomes, config.colony, maxColonies);
  const pathing = new GreedyPathing(grid);
  const geneConfig = config.gene ?? defaultGeneConfig();
  const genes = new GeneInventory(maxColonies, geneConfig);

  // --- Autonomy: the task scheduler + the decision engine. ---
  const aiConfig = config.ai ?? defaultColonyAIConfig();
  const tasks = new TaskQueue(maxColonies, capacity, aiConfig.tasksPerColony);
  // Per-tick AI decisions draw from a stream isolated from worldgen (DESIGN §6),
  // matching buildColonyWorld/buildEcosystemWorld — same AI_STREAM_SALT.
  const aiRng = new Rng((config.seed ^ AI_STREAM_SALT) >>> 0);
  const ai = new ColonyAISystem(
    colonies, entities, grid, aiRng, tasks, genes, aiConfig, config.colony.maxPopulation,
  );

  // --- Evolution-Box economy: an INDEPENDENT RNG stream so box draws never
  //     perturb colony determinism. Built BEFORE ctx so ctx.mutation can point
  //     at it (kill energy flows in via the MutationEnergySink seam). ---
  const evolutionRng = new Rng((config.seed ^ 0x85ebca6b) >>> 0);
  const energySource = buildEnergySource(colonies);
  const clock = { tick: 0 };
  const evolution = new EvolutionBoxSystem(
    genes, evolutionRng, config.evolution, energySource, maxColonies, clock,
  );

  // The organism loop reads the autonomy-widened ctx; ctx.mutation (an
  // OrganismCtx field) routes kill energy into the box economy. AutonomyCtx
  // mirrors OrganismCtx and is assignable to it, so we type the literal as the
  // intersection that carries `mutation` — assignable to AutonomyCtx (for
  // OrganismSystem) AND exposing mutation (read by killOrganism). The SAME clock
  // object is shared so openBox stamps forged genes with the live tick
  // (OrganismSystem publishes it first each tick).
  const ctx: AutonomyCtx & { readonly mutation: MutationEnergySink } = {
    entities, grid, rng, colonies, resources, genomes, pathing,
    clock, genes, geneConfig,
    // Per-creature movement speed (opt-in; mirrors buildColonyWorld). Off by default
    // ⇒ original integer stepsPerTick movement, byte-identical.
    creatureSpeed: config.colony.creatureSpeed === true,
    tasks, onExpand: ai.onExpand, onScout: ai.onScout,
    mutation: evolution,
  };
  const organisms = new OrganismSystem(ctx);

  // --- Sparse walls (~6%), deterministic; drawn first so placement avoids them.
  // Identical to buildColonyWorld (same RNG draws, same order).
  const wallTarget = Math.floor(grid.size * 0.06);
  let wallsPlaced = 0;
  let guard = 0;
  const guardMax = grid.size * 4;
  while (wallsPlaced < wallTarget && guard++ < guardMax) {
    const x = rng.int(config.width);
    const y = rng.int(config.height);
    if (grid.isPassable(x, y)) {
      grid.setPassable(x, y, false);
      wallsPlaced++;
    }
  }

  // --- Food nodes on passable tiles (colony RNG). ---
  placeOnPassable(rng, grid, config.food, (x, y) => {
    resources.addFood(x, y, config.foodAmount);
  });

  // --- Colony nests + founding members on passable tiles (colony RNG). ---
  placeOnPassable(rng, grid, config.colonies, (x, y) => {
    const cid = colonies.createColony(x, y);
    colonies.seedInitialMembers(cid, config.startMembers);
  });

  // --- Fixed system order: ai → scheduler → organisms → colonies → resources →
  //     evolution-box (LAST: it spends energy that kills/AI moved this tick). ---
  const scheduler: System = {
    name: 'task-scheduler',
    update(_tick: Tick): void {
      tasks.assignOpenTasks(ctx);
    },
  };

  // Read-only colony-evolution leaderboard (observes only; runs LAST), mirroring
  // buildColonyWorld so the shared computeColonyStateHash (which folds board
  // standings) accepts this world.
  const leaderboard = new LeaderboardSystem(
    colonies, ai, genes, config.leaderboard ?? defaultLeaderboardConfig(),
  );

  const loop = new GameLoop();
  loop
    .add(ai)
    .add(scheduler)
    .add(organisms)
    .add(colonies)
    .add(resources)
    .add(evolution)
    .add(leaderboard);

  return {
    config: config as ColonyWorld['config'],
    rng, entities, grid, loop, resources, colonies, organisms,
    genes, genomes, tasks, ai, leaderboard,
    evolution, evolutionRng,
    creatureSpeed: config.colony.creatureSpeed === true,
  };
}

/** Find a passable tile (deterministic, RNG-driven) and invoke `place(x,y)`. */
function placeOnPassable(
  rng: Rng,
  grid: WorldGrid,
  count: number,
  place: (x: number, y: number) => void,
): void {
  for (let i = 0; i < count; i++) {
    let x = 0;
    let y = 0;
    let tries = 0;
    do {
      x = rng.int(grid.width);
      y = rng.int(grid.height);
      tries++;
    } while (!grid.isPassable(x, y) && tries < 64);
    if (grid.isPassable(x, y)) place(x, y);
  }
}

/** Advance the world by `ticks` ticks. */
export function runHeadless(world: EvolutionWorld, ticks: number): void {
  world.loop.step(ticks);
}

// ---------------------------------------------------------------------------
// Deterministic state fingerprint (colony + organism + gene + box economy).
// ---------------------------------------------------------------------------

export function computeEvolutionStateHash(world: EvolutionWorld): number {
  // The colony hash already folds the FULL entity set + colony/ai/tasks/genes.
  let h = computeColonyStateHash(world);

  const evo = world.evolution;
  const colonies = world.colonies;

  // Per-colony box economy state, in fixed colony order.
  for (let cid = 0; cid < colonies.count; cid++) {
    h = fnvU32(h, evo.energyAt(cid));
    h = fnvU32(h, evo.tierAt(cid));
    h = fnvU32(h, evo.boxesOpenedAt(cid));
    h = fnvU32(h, evo.boxesAtTierAt(cid));
    h = fnvU32(h, evo.opensSinceRareAt(cid));
    h = fnvU32(h, evo.raresEarnedAt(cid));
    h = fnvU32(h, evo.energyEarnedAt(cid));
    h = fnvU32(h, evo.sacrificedAt(cid));
  }

  // Global counters.
  h = fnvU32(h, evo.boxesOpenedTotal);
  h = fnvU32(h, evo.genesGrantedTotal);
  h = fnvU32(h, evo.raresGrantedTotal);
  h = fnvU32(h, evo.pityTriggersTotal);
  h = fnvU32(h, evo.sacrificedGenesTotal);
  h = fnvU32(h, evo.energyFromKillsTotal);
  h = fnvU32(h, evo.energyFromExploreTotal);
  return h >>> 0;
}

// ---------------------------------------------------------------------------
// Census / debug.
// ---------------------------------------------------------------------------

/** Short label per BOX_TIER.* (Common, Apex, Miracle). */
const TIER_NAME: readonly string[] = ['Common', 'Apex', 'Miracle'];

/**
 * Per-colony box-economy census (CLI): mutation energy, current tier, lifetime
 * boxes opened, rares earned, duplicates sacrificed. Fixed colony order.
 */
export function evoCensus(world: EvolutionWorld): string {
  const evo = world.evolution;
  const c = world.colonies;
  let s = '';
  for (let cid = 0; cid < c.count; cid++) {
    s +=
      `  evo#${cid} energy=${String(evo.energyAt(cid)).padStart(5)} ` +
      `tier=${(TIER_NAME[evo.tierAt(cid)] as string).padEnd(7)} ` +
      `boxes=${String(evo.boxesOpenedAt(cid)).padStart(4)} ` +
      `rares=${String(evo.raresEarnedAt(cid)).padStart(3)} ` +
      `sac=${String(evo.sacrificedAt(cid)).padStart(4)} ` +
      `genes=${String(world.genes.countOf(cid)).padStart(4)}\n`;
  }
  return s;
}

// ---------------------------------------------------------------------------
// CLI wrapper (mirrors ecosystemExample).
// ---------------------------------------------------------------------------

interface CliFlags {
  seed: number; width: number; height: number; colonies: number; members: number;
  food: number; ticks: number; every: number; quiet: boolean; map: boolean;
}

function parseFlags(argv: readonly string[]): CliFlags {
  const f: CliFlags = {
    seed: 1, width: 48, height: 24, colonies: 2, members: 8,
    food: 28, ticks: 300, every: 50, quiet: false, map: false,
  };
  for (let i = 0; i < argv.length; i++) {
    const a = argv[i] as string;
    switch (a) {
      case '--seed': f.seed = intArg(argv, ++i, f.seed); break;
      case '--width': f.width = intArg(argv, ++i, f.width); break;
      case '--height': f.height = intArg(argv, ++i, f.height); break;
      case '--colonies': f.colonies = intArg(argv, ++i, f.colonies); break;
      case '--members': f.members = intArg(argv, ++i, f.members); break;
      case '--food': f.food = intArg(argv, ++i, f.food); break;
      case '--ticks': f.ticks = intArg(argv, ++i, f.ticks); break;
      case '--every': f.every = intArg(argv, ++i, f.every); break;
      case '--quiet': f.quiet = true; break;
      case '--map': f.map = true; break;
      default: break;
    }
  }
  return f;
}

function intArg(argv: readonly string[], i: number, fallback: number): number {
  const raw = argv[i];
  if (raw === undefined) return fallback;
  const v = Number.parseInt(raw, 10);
  return Number.isFinite(v) ? v : fallback;
}

function configFromFlags(f: CliFlags): ColonyWorldConfig & { evolution: EvolutionBoxConfig } {
  return {
    seed: f.seed, width: f.width, height: f.height,
    colonies: f.colonies, startMembers: f.members,
    food: f.food, foodAmount: 16, maxPerTile: 4,
    castes: defaultCasteConfigs(), colony: defaultColonyConfig(),
    resource: { regenDelay: 14, regenAmount: 16 },
    evolution: defaultEvolutionBoxConfig(),
  };
}

function runCli(argv: readonly string[]): void {
  const flags = parseFlags(argv);
  const config = configFromFlags(flags);
  const world = buildEvolutionWorld(config);

  if (flags.quiet) {
    const start = process.hrtime.bigint();
    runHeadless(world, flags.ticks);
    const elapsedMs = Number(process.hrtime.bigint() - start) / 1e6;
    console.log(
      `quiet: colonies=${flags.colonies} ticks=${flags.ticks} grid=${flags.width}x${flags.height} | ` +
        `elapsed=${elapsedMs.toFixed(2)}ms ticks/sec=${(flags.ticks / (elapsedMs / 1000)).toFixed(0)} ` +
        `boxes=${world.evolution.boxesOpenedTotal} rares=${world.evolution.raresGrantedTotal} ` +
        `pity=${world.evolution.pityTriggersTotal} sac=${world.evolution.sacrificedGenesTotal} ` +
        `organisms=${world.colonies.totalPopulation()} ` +
        `hash=${hashHex(computeEvolutionStateHash(world))}`,
    );
    return;
  }

  const small = world.grid.width <= 80 && world.grid.height <= 48;
  printFrame(world, flags, small);
  let done = 0;
  while (done < flags.ticks) {
    const chunk = Math.min(flags.every, flags.ticks - done);
    runHeadless(world, chunk);
    done += chunk;
    printFrame(world, flags, small);
  }
  console.log(
    `\nsummary: seed=${flags.seed} ticks=${world.loop.tick} ` +
      `boxes=${world.evolution.boxesOpenedTotal} genes=${world.evolution.genesGrantedTotal} ` +
      `rares=${world.evolution.raresGrantedTotal} pity=${world.evolution.pityTriggersTotal} ` +
      `sac=${world.evolution.sacrificedGenesTotal} ` +
      `energy(kill=${world.evolution.energyFromKillsTotal} explore=${world.evolution.energyFromExploreTotal}) ` +
      `finalHash=${hashHex(computeEvolutionStateHash(world))}`,
  );
}

function printFrame(world: EvolutionWorld, flags: CliFlags, small: boolean): void {
  console.log(
    `tick=${String(world.loop.tick).padStart(4)} ` +
      `organisms=${String(world.colonies.totalPopulation()).padStart(4)} ` +
      `boxes=${String(world.evolution.boxesOpenedTotal).padStart(4)} ` +
      `rares=${String(world.evolution.raresGrantedTotal).padStart(4)} ` +
      `sac=${String(world.evolution.sacrificedGenesTotal).padStart(5)}`,
  );
  process.stdout.write(evoCensus(world));
  if (flags.map && small) process.stdout.write(renderColony(world));
}

const isMain =
  typeof process !== 'undefined' &&
  Array.isArray(process.argv) &&
  process.argv[1] !== undefined &&
  import.meta.url === `file://${process.argv[1]}`;

if (isMain) runCli(process.argv.slice(2));

// Re-export so tests/CLI can reach tiers/sentinels without importing types.
export { BOX_TIER_COUNT, NIL };
